import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import type { CallFilters, McpCallFacets, OpsCallsResponse, OpsMcpCall, OpsPlanChange, OpsPlanChangesResponse, StoredPlanSummary } from "./types";

const EVE_HOST: string = import.meta.env.VITE_AMY_EVE_HOST || "http://127.0.0.1:2000";

type GetToken = () => Promise<string | null>;

async function getJson<T>(getToken: GetToken, path: string, params: Record<string, string | null | undefined>): Promise<T> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) qs.set(key, value);
  const token = await getToken();
  const response = await fetch(`${EVE_HOST}${path}?${qs}`, { headers: { Authorization: `Bearer ${token ?? ""}` } });
  const body = (await response.json().catch(() => null)) as (T & { ok?: boolean; error?: string }) | null;
  if (!response.ok || !body?.ok) throw new Error(body?.error ?? `Could not reach Amy (${response.status})`);
  return body;
}

const message = (err: unknown) => (err instanceof Error ? err.message : "Could not reach Amy");

/**
 * The CS MCP call log (GET /eve/v1/ops/activity/calls), newest first: the
 * first page carries the facets (callers, tools, orgs, the last 24 hours);
 * `loadMore` appends the next older page. A filter change starts over.
 */
export function useMcpCalls(enabled: boolean, filters: CallFilters) {
  const { getToken } = useAuth();
  const [calls, setCalls] = useState<OpsMcpCall[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [facets, setFacets] = useState<McpCallFacets | null>(null);
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);
  const { caller, org_id, tool, status, changes } = filters;

  const params = useCallback(
    (cursor: string | null) => ({ caller, org_id, tool, status, changes: changes ? "1" : null, cursor, facets: cursor ? null : "1" }),
    [caller, org_id, tool, status, changes]
  );

  const refresh = useCallback(async () => {
    const mySeq = ++seq.current;
    setLoading(true);
    try {
      const body = await getJson<OpsCallsResponse>(getToken, "/eve/v1/ops/activity/calls", params(null));
      if (mySeq !== seq.current) return;
      setCalls(body.calls);
      setNext(body.next);
      setFacets(body.facets);
      setOrgNames(body.org_names);
      setError(null);
    } catch (err) {
      if (mySeq === seq.current) setError(message(err));
    } finally {
      if (mySeq === seq.current) setLoading(false);
    }
  }, [getToken, params]);

  const loadMore = useCallback(async () => {
    if (!next) return;
    const mySeq = seq.current;
    setLoadingMore(true);
    try {
      const body = await getJson<OpsCallsResponse>(getToken, "/eve/v1/ops/activity/calls", params(next));
      if (mySeq !== seq.current) return; // the filters changed meanwhile
      setCalls((prev) => [...prev, ...body.calls]);
      setNext(body.next);
      setOrgNames((prev) => ({ ...prev, ...body.org_names }));
    } catch (err) {
      if (mySeq === seq.current) setError(message(err));
    } finally {
      setLoadingMore(false);
    }
  }, [getToken, next, params]);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  return { calls, next, facets, orgNames, error, loading, loadingMore, refresh, loadMore };
}

/** The client plans' change history (GET /eve/v1/ops/activity/plans), newest first, with every stored plan's current version. */
export function usePlanChanges(enabled: boolean, slug: string | null) {
  const { getToken } = useAuth();
  const [changes, setChanges] = useState<OpsPlanChange[]>([]);
  const [plans, setPlans] = useState<StoredPlanSummary[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    const mySeq = ++seq.current;
    setLoading(true);
    try {
      const body = await getJson<OpsPlanChangesResponse>(getToken, "/eve/v1/ops/activity/plans", { slug });
      if (mySeq !== seq.current) return;
      setChanges(body.changes);
      setPlans(body.plans);
      setNext(body.next);
      setError(null);
    } catch (err) {
      if (mySeq === seq.current) setError(message(err));
    } finally {
      if (mySeq === seq.current) setLoading(false);
    }
  }, [getToken, slug]);

  const loadMore = useCallback(async () => {
    if (!next) return;
    const mySeq = seq.current;
    setLoadingMore(true);
    try {
      const body = await getJson<OpsPlanChangesResponse>(getToken, "/eve/v1/ops/activity/plans", { slug, cursor: next });
      if (mySeq !== seq.current) return;
      setChanges((prev) => [...prev, ...body.changes]);
      setNext(body.next);
    } catch (err) {
      if (mySeq === seq.current) setError(message(err));
    } finally {
      setLoadingMore(false);
    }
  }, [getToken, next, slug]);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  return { changes, plans, next, error, loading, loadingMore, refresh, loadMore };
}
